import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
export function useStudents(filters = {}) {
    return useQuery({
        queryKey: ['students', filters],
        queryFn: () => {
            const params = new URLSearchParams();
            if (filters.opposition_id)
                params.set('opposition_id', filters.opposition_id);
            if (filters.shift_id)
                params.set('shift_id', filters.shift_id);
            if (filters.status)
                params.set('status', filters.status);
            const qs = params.toString();
            return apiClient(`/students${qs ? `?${qs}` : ''}`);
        },
        staleTime: 1000 * 60 * 3,
    });
}
export function useStudent(id) {
    return useQuery({
        queryKey: ['students', id],
        queryFn: () => apiClient(`/students/${id ?? ''}`),
        enabled: !!id,
    });
}
export function useCreateStudent() {
    const queryClient = useQueryClient();
    return useMutation({
        // Manual creation returns the temporary password so the director can relay
        // credentials to the student (the student does not pick their own password here).
        mutationFn: (dto) => apiClient('/students', {
            method: 'POST',
            body: JSON.stringify(dto),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['students'] }),
    });
}
export function useUpdateStudent(id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (dto) => apiClient(`/students/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['students', id] });
            void queryClient.invalidateQueries({ queryKey: ['students'] });
            void queryClient.invalidateQueries({ queryKey: ['students', id, 'full-profile'] });
        },
    });
}
export function useUpdateStudentStatus(id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (status) => apiClient(`/students/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['students'] });
            void queryClient.invalidateQueries({ queryKey: ['students', id, 'full-profile'] });
        },
    });
}
export function useDeleteStudent() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiClient(`/students/${id}`, { method: 'DELETE' }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['students'] }),
    });
}
/** A student's attendance summary in the calling professor's own classes. */
export function useProfessorStudentAttendance(studentId) {
    return useQuery({
        queryKey: ['professor-student-attendance', studentId],
        queryFn: () => apiClient(`/attendance/professor/student/${studentId}`),
        enabled: !!studentId,
        staleTime: 1000 * 60,
    });
}
export function useResetStudentPassword(id) {
    return useMutation({
        mutationFn: () => apiClient(`/students/${id}/reset-password`, { method: 'POST' }),
    });
}
export function useCloseStudentSessions(id) {
    return useMutation({
        mutationFn: () => apiClient(`/students/${id}/close-sessions`, { method: 'POST' }),
    });
}
/**
 * Fuzzy search of students by number, name, DNI or email.
 * Backend filters by role: directors see all, professors only their shifts.
 */
export function useSearchStudents(query, limit = 10) {
    return useQuery({
        queryKey: ['students', 'search', query, limit],
        queryFn: () => {
            const params = new URLSearchParams({ q: query, limit: String(limit) });
            return apiClient(`/students/search?${params.toString()}`);
        },
        enabled: query.trim().length >= 1,
        staleTime: 1000 * 30,
    });
}
/** Suggests the next free student_number to pre-fill the create form. */
export function useNextStudentNumber(enabled = true) {
    return useQuery({
        queryKey: ['students', 'next-number'],
        queryFn: () => apiClient('/students/next-number'),
        enabled,
        staleTime: 1000 * 60,
    });
}
/**
 * Aggregated student profile (data + KPIs). Used by both the preview modal
 * and the full ficha page header. Tab data comes from separate hooks.
 */
export function useStudentFullProfile(id) {
    return useQuery({
        queryKey: ['students', id, 'full-profile'],
        queryFn: () => apiClient(`/students/${id}/full-profile`),
        enabled: !!id,
        staleTime: 1000 * 60,
    });
}
//# sourceMappingURL=useStudents.js.map